import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { BlankSessionService } from '@helios/sessions'
import { RootState, selectSelectedReconDate } from '@helios/state-management'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { DisplayDateFormat } from '@northfork/basics'
import { ToastService, ToggleStates } from '@northfork/ng-basics'
import { Observable, Subject } from 'rxjs'
import { take, takeUntil } from 'rxjs/operators'
import {
  Break,
  BreakNoteHandling,
  BreakResponseObject,
  CodeAvailability,
} from '../../model/break.model'
import {
  actionCodeOptions,
  breakDialogValidation,
  BreakDialogValidation,
  computeStaleCodeHints,
  descriptionCodeOptions,
  notesRequiredActionCodes,
  prefillSelection,
} from '../../validators/break-code-validation'
import { resolvePaste } from '../../model/break-code-paste'
import { isPasteBreakCodesEvent } from '../../utils/paste-break-codes-event'
import { BreakCodeClipboardService } from '../../services/break-code-clipboard.service'
import { MultiBreaksService } from '../../services/multi-breaks.service'
import {
  firstHoldTimeErrorMessage,
  HoldExpirationDateValidator,
  HoldExpirationDaysValidator,
  HoldModalValidationMessages,
} from '../../validators/hold-validators'
import { NoteEditorComponent } from '../note-editor/note-editor.component'
import { BREAK_DIALOG_SHARED_STYLES } from '../break-dialog-shared.styles'

@Component({
  selector: 'hs-hold-modal',
  templateUrl: './hold-modal.component.html',
  styles: [
    BREAK_DIALOG_SHARED_STYLES,
    `
      .modal-body {
        max-height: calc(65vh / var(--hs-ui-scale, 1));
        overflow-y: auto;
      }
      .expires-row {
        display: flex;
        gap: 40px;
        align-items: flex-start;
        margin-bottom: 4px;
      }
      .expires-row nf-form-control {
        margin-bottom: 0;
      }
      .expires-row .expires-value {
        flex: 1;
        min-width: 0;
      }
      .field-row nf-form-control {
        margin-bottom: 0;
      }
      .expires-value ::ng-deep input {
        font-size: 12px;
      }
      .modal-body ::ng-deep .ng-select,
      .modal-body ::ng-deep .ng-dropdown-panel .ng-option,
      .modal-body ::ng-deep textarea {
        font-size: 12px;
      }
    `,
  ],
})
export class HoldModalComponent implements OnInit, OnDestroy {
  @ViewChild('noteEditor') noteEditor!: NoteEditorComponent

  private _breaks: Break[] = []
  private readonly _currentDate = new Date()
  private _availability: CodeAvailability = { status: 'unavailable' }
  actionCodes: string[] = []
  descriptionCodes: string[] = []
  notesRequiredActionCodeSet = new Set<string>()
  breakIds: string[] = []
  modalTitle = 'Hold Break'
  saveBtnLabel = 'Save'
  newNote = ''
  noteHandling = BreakNoteHandling.REPLACE
  hasExistingNote = true
  restrictedBreaks: string[] = []

  @Input() set breaks(values: Break[]) {
    if (values.length === 0) {
      this.activeModal.dismiss()
    }

    this._breaks = values
    this.breakIds = values.map(b => b.identifier)

    this.updateActionFromBreaks()

    this.updateDescriptionFromBreaks()

    this.updateCodeValidators()

    if (values.length > 1) {
      this.modalTitle = `Hold Breaks (${values.length})`
      this.saveBtnLabel = `Save (${values.length})`
    }

    const allHoldLimitsInDays = values
      .map(x => x.actionLimits?.holdLimitInDays ?? null)
      .filter(x => x != null)

    if (allHoldLimitsInDays.length > 0) {
      this.holdLimitInDays = Math.min(...allHoldLimitsInDays)
    }
  }
  get breaks(): Break[] {
    return this._breaks
  }

  @Input() set availability(value: CodeAvailability) {
    this._availability = value
    this.actionCodes = actionCodeOptions(value)
    this.descriptionCodes = descriptionCodeOptions(value)
    this.notesRequiredActionCodeSet = notesRequiredActionCodes(value)
    this.updateActionFromBreaks()
    this.updateDescriptionFromBreaks()
    this.updateCodeValidators()
  }
  get availability(): CodeAvailability {
    return this._availability
  }

  // Make a configured-but-unselected code fail form validation (single break
  // only) so the Save button greys out, matching the required-note behavior.
  private updateCodeValidators() {
    const single = this._breaks.length === 1
    this.action.setValidators(
      single && this.actionCodes.length > 0 ? [Validators.required] : [],
    )
    this.description.setValidators(
      single && this.descriptionCodes.length > 0 ? [Validators.required] : [],
    )
    this.action.updateValueAndValidity()
    this.description.updateValueAndValidity()
  }

  get validation(): BreakDialogValidation {
    return breakDialogValidation({
      breaks: this.breaks,
      availability: this._availability,
      selectedActionCode: this.action.value ?? null,
      selectedDescriptionCode: this.description.value ?? null,
      note: this.newNote,
    })
  }

  /** Message for an invalid expiry control, or null when valid. */
  get expiresError(): string | null {
    for (const ctrl of [this.expiresInDays, this.expiresOnDate]) {
      if (ctrl.enabled && ctrl.invalid && ctrl.errors) {
        return firstHoldTimeErrorMessage(ctrl.errors)
      }
    }
    return null
  }

  /**
   * Why Save is disabled, for the tooltip: a code/note blocker if any, else the
   * expiry error (which form.invalid alone wouldn't surface, esp. compact).
   */
  get saveDisabledReason(): string {
    if (this.validation.submitDisabled) {
      return this.validation.reasons[0] ?? ''
    }
    return this.expiresError ?? ''
  }

  get staleCodeHints(): string[] {
    return computeStaleCodeHints(this.breaks, this._availability)
  }

  @Input() set days(value: number | undefined) {
    if (value == undefined) return

    this.expiresInDays.setValue(value)
  }

  @Input() set date(value: Date | undefined) {
    if (value == undefined) return

    this.expiresOnDate.setValue(value)
  }

  @Input() set daysOrDate(value: 'days' | 'date' | undefined) {
    if (value == undefined) return

    this.expiresToggle.setValue(value === 'date')
  }

  formSubmitting = false
  dateFormat = DisplayDateFormat

  expiresInDays = new FormControl(1, [Validators.required])
  //Expires Toggle false = expires in days, true = expire on date.
  expiresToggle = new FormControl(false, [])
  expiresOnDate = new FormControl(new Date(), [])
  action = new FormControl(undefined)
  description = new FormControl(undefined)

  form = new FormGroup({
    expiresInDays: this.expiresInDays,
    expiresOnDate: this.expiresOnDate,
    expiresToggle: this.expiresToggle,
    action: this.action,
    description: this.description,
  })

  expiresToggleStates: ToggleStates = {
    on: 'Specific Date',
    off: 'Expire in Days',
  }

  set holdLimitInDays(daysLimit: number | undefined) {
    if (daysLimit == undefined) {
      return
    }
    const dateLimit = new Date(this._currentDate)
    dateLimit.setDate(dateLimit.getDate() + daysLimit)

    this.expiresInDays.setValidators([
      Validators.required,
      HoldExpirationDaysValidator(daysLimit),
    ])
    this.expiresOnDate.setValidators([
      Validators.required,
      HoldExpirationDateValidator(this._currentDate, dateLimit),
    ])
  }

  validationMessages = HoldModalValidationMessages

  private readonly destroySubject$ = new Subject<void>()

  constructor(
    public activeModal: NgbActiveModal,
    public toastService: ToastService,
    private readonly breaksService: MultiBreaksService,
    private readonly blankSessionService: BlankSessionService,
    private readonly store: Store<RootState>,
    private readonly breakCodeClipboard: BreakCodeClipboardService,
    private readonly host: ElementRef<HTMLElement>,
  ) {}

  ngOnInit() {
    this.expiresToggle.valueChanges
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(expiresOnDate => this.onExpiresToggleChange(expiresOnDate))

    this.hasExistingNote = this.breaks.some(b => b.note != null)

    this.restrictedBreaks = this.breaks
      .filter(b => b.restrictedAction === 'HOLD,ACCEPT')
      .map(b => b.identifier)
  }

  ngOnDestroy() {
    this.destroySubject$.next()
    this.destroySubject$.complete()
  }

  onNoteEditorFormReady(noteEditorForm: FormGroup) {
    this.form.addControl('noteEditor', noteEditorForm)
  }

  onNoteHandlingChange(noteHandling: BreakNoteHandling) {
    this.noteHandling = noteHandling
  }

  async onSubmit() {
    if (this.validation.submitDisabled) return
    this.formSubmitting = true

    await this.blankSessionService.createBlankSessionIfNotInSession()

    let advanceResolve$: Observable<BreakResponseObject>
    if (this.expiresToggle.value) {
      advanceResolve$ = this.breaksService.holdToDate(
        this.breakIds,
        this.expiresOnDate.value,
        this.newNote,
        this.noteHandling,
        this.restrictedBreaks,
        this.action.value,
        this.description.value,
      )
    } else {
      const reconDate$ = this.store
        .select(selectSelectedReconDate)
        .pipe(take(1))
      advanceResolve$ = this.breaksService.holdForDays(
        this.breakIds,
        this.expiresInDays.value,
        this.newNote,
        this.noteHandling,
        this.restrictedBreaks,
        await reconDate$.toPromise(),
        this.action.value,
        this.description.value,
      )
    }
    advanceResolve$.pipe(take(1)).subscribe(
      response => {
        this.formSubmitting = false
        this.activeModal.close(response)
      },
      () => {
        this.formSubmitting = false
        this.toastService.error('Hold break command failed, request cancelled.')
        this.activeModal.dismiss(null)
      },
    )
  }

  get canPaste(): boolean {
    return this.breakCodeClipboard.hasContent
  }

  /**
   * Fill the code + note boxes from the in-app clipboard. Codes go to THIS component's
   * `action`/`description`: NoteEditorComponent.form also carries action/description
   * controls this dialog never reads — writing those would silently do nothing.
   */
  onPasteBreakCodes(): void {
    const entry = this.breakCodeClipboard.read()
    if (!entry) {
      return
    }

    const result = resolvePaste(entry, this.actionCodes, this.descriptionCodes)
    this.action.setValue(result.actionCode)
    this.description.setValue(result.descriptionCode)
    this.noteEditor.form.controls.note.setValue(result.note)
    // Mirrors NoteEditorComponent's own invariant: a non-empty note must not sit in a
    // collapsed, invisible box, or the user saves a note the UI said wasn't there.
    if (result.note) {
      this.noteEditor.noteExpanded = true
    }

    if (result.error) {
      this.toastService.error(result.error)
    }
  }

  /** alt+v — see isPasteBreakCodesEvent for why this is not a hotkeys-registry binding. */
  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!isPasteBreakCodesEvent(event, this.host.nativeElement)) {
      return
    }
    event.preventDefault()
    this.onPasteBreakCodes()
  }

  /**
   * Just close the modal and reset the form
   */
  onCancel() {
    this.activeModal.dismiss()
  }

  onExpiresToggleChange(expiresOnDate: boolean) {
    if (expiresOnDate) {
      this.expiresOnDate.enable()
      this.expiresInDays.disable()
      return
    }

    this.expiresOnDate.disable()
    this.expiresInDays.enable()
  }

  onNoteChange(newNote: string) {
    this.newNote = newNote
  }

  private updateActionFromBreaks() {
    const value = prefillSelection(
      this._breaks.map(b => b.actionCode),
      this.actionCodes,
    )
    if (value) this.action.setValue(value)
  }

  private updateDescriptionFromBreaks() {
    const value = prefillSelection(
      this._breaks.map(b => b.descriptionCode),
      this.descriptionCodes,
    )
    if (value) this.description.setValue(value)
  }
}
